import React, { useState, useEffect } from "react";
import axios from "axios";

const BlogDetails = () => {
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchBlogData = async () => {
      try {
        const config = {
          method: "get",
          url: `${process.env.REACT_APP_API_URL}/news-blogs/news-blog-id/68cbd28e638e92767a42d9e3`,
        };

        const response = await axios.request(config);
        setBlog(response.data.newsBlog);
      } catch (err) {
        setError("Failed to fetch blog data");
        console.error("Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogData();
  }, []);

  const nextImage = () => {
    if (blog?.images) {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === blog.images.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  const prevImage = () => {
    if (blog?.images) {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === 0 ? blog.images.length - 1 : prevIndex - 1
      );
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse">
          <div className="bg-skeletonLoaderColor h-8 w-64 rounded mb-4"></div>
          <div className="bg-skeletonLoaderColor h-4 w-48 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-2xl mb-4">⚠️</div>
          <h2 className="text-xl font-poppins text-primaryTextColor">
            {error}
          </h2>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-poppins text-primaryTextColor">
            Blog not found
          </h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 md:px-8 lg:px-12 py-10 font-subheading">
      {/* Header */}
      <header className="mb-8">
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading text-primaryTextColor text-center">
          {blog.title}
        </h1>
      </header>

      <div className="">
        {/* Blog Meta Information */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-primaryBtnColor rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">
                  {blog.author.charAt(0)}
                </span>
              </div>
              <div>
                <p className="font-semibold text-secondaryTextColor">
                  {blog.author}
                </p>
                <p className="text-gray-500 text-sm">
                  {formatDate(blog.createdAt)}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {blog.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-primaryBtnColor text-white text-sm rounded-full hover:bg-primaryBtnHoverColor transition-colors duration-300"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          {/* Image Gallery */}
          {blog.images && blog.images.length > 0 && (
            <div className="relative mb-6 rounded-lg overflow-hidden">
              <div className="aspect-w-16 aspect-h-9 bg-gray-200">
                <img
                  src={blog.images[currentImageIndex]}
                  alt={blog.title}
                  className="w-full h-64 md:h-96 object-cover transition-opacity duration-300"
                />
              </div>

              {/* Image Navigation */}
              {blog.images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all duration-300"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all duration-300"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>

                  {/* Image Indicators */}
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                    {blog.images.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-3 h-3 rounded-full transition-all duration-300 ${
                          index === currentImageIndex
                            ? "bg-white"
                            : "bg-white bg-opacity-50"
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Blog Content */}
          <div className="prose prose-lg max-w-none">
            <div className="text-secondaryTextColor leading-relaxed whitespace-pre-line">
              {blog.content.split("\n").map((paragraph, index) => (
                <p key={index} className="mb-4 text-gray-700">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 text-sm text-gray-600">
              <div>
                <span className="font-semibold">Last Updated:</span>{" "}
                {formatDate(blog.updatedAt)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Back to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className="fixed bottom-8 right-8 bg-primaryBtnColor text-white p-3 rounded-full shadow-lg hover:bg-primaryBtnHoverColor transition-all duration-300 z-50"
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 15l7-7 7 7"
          />
        </svg>
      </button>
    </div>
  );
};

export default BlogDetails;
